"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { format } from "date-fns";
import { ChevronRight, Mail } from "lucide-react";
import Link from "next/link";
import SectionHeading from "@/components/SectionHeading";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription logic here
    console.log("Subscribing email:", email);
    // Reset form
    setEmail("");
    // Show success message or toast notification
  };

  // Sample latest articles data - replace with your actual data
  const latestArticles = [
    {
      id: 1,
      title: "Hello world: how I built this site",
      date: new Date("2022-04-21"),
      description: "I originally built this portfolio site back in 2018, and since then it's evolved quite a bit. Recently I migrated from Create React App to Next.js and made some major upgrades in the process.",
      readTime: "00:05:03:99",
      featured: false,
      content: "I originally built this portfolio site back in 2018, and since then it's evolved quite a bit. Recently I migrated from Create React App to Next.js and made some major upgrades in the process. This article details the journey, the challenges faced, and the solutions implemented. The site now loads faster, has better SEO, and is more maintainable.",
    },
    {
      id: 2,
      title: "You (probably) don't need CSS-in-JS",
      date: new Date("2022-05-01"),
      description: "Vanilla CSS is good now actually. Here's a couple nifty techniques for dynamically styling React components with CSS custom properties.",
      readTime: "00:07:53:60",
      featured: true,
      content: "Vanilla CSS is good now actually. Here's a couple nifty techniques for dynamically styling React components with CSS custom properties. With the advent of CSS variables and modern browser support, many of the problems that CSS-in-JS was designed to solve are now addressable with pure CSS.",
    },
    {
      id: 3,
      title: "Building a responsive design system",
      date: new Date("2022-06-15"),
      description: "How to create a consistent design system that works across all device sizes and maintains visual harmony.",
      readTime: "00:06:22:45",
      featured: false,
      content: "How to create a consistent design system that works across all device sizes and maintains visual harmony. This article explores the principles of responsive design, the importance of a coherent visual language, and practical implementation techniques.",
    }
  ];

  return (
    <section id="articles" className="py-20 px-6 bg-background">
      <div className="container max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Articles List Section */}
          <div className="lg:col-span-5">
            <SectionHeading title="Latest articles" />
            
            <div className="space-y-6">
              {latestArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  className="relative border-b border-border pb-6"
                >
                  {article.featured && (
                    <div className="absolute -right-2 -top-2 z-10">
                      <Badge className="bg-accent text-accent-foreground">Featured</Badge>
                    </div>
                  )}
                  <div className="mb-2 flex items-center text-sm text-accent">
                    <div className="h-1 w-10 bg-accent mr-2"></div>
                    {format(article.date, "MMMM dd, yyyy")}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{article.title}</h3>
                  <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{article.description}</p>
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" className="pl-0 hover:pl-2 transition-all">
                      <span>Read</span>
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">{article.readTime}</span>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <Button variant="outline" className="group" asChild>
                <Link href="/articles">
                  <span>Find more articles</span>
                  <ChevronRight className="ml-1 h-4 w-4 group-hover:ml-2 transition-all" />
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Stay in Touch Section */}
          <div className="lg:col-span-7 relative">
            <div className="sticky top-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="h-[450px] flex flex-col"
              >
                <Card className="border border-border overflow-hidden h-full flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-2xl">Stay in touch</CardTitle>
                    <CardDescription className="text-base">
                      Subscribe to my newsletter to receive updates on new articles, projects, and tech insights.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <div className="flex flex-col justify-center h-full">
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="flex flex-col space-y-2">
                          <label htmlFor="email" className="text-sm font-medium">Email address</label>
                          <div className="flex items-center bg-background rounded-lg border border-border p-1 pl-3">
                            <Mail className="h-5 w-5 text-muted-foreground mr-2" />
                            <Input
                              id="email"
                              type="email"
                              placeholder="your.email@example.com"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </div>
                        </div>
                        <Button type="submit" className="w-full">
                          Subscribe
                        </Button>
                      </form>
                    </div>
                  </CardContent>
                  <CardFooter className="flex-col items-start border-t pt-4">
                    <p className="text-sm text-muted-foreground">
                      No spam, ever. I&apos;ll email you only when I have something valuable to share.
                    </p>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter; 